import { useCallback } from "react";
import { matchPath, useNavigate } from "react-router";

import { organisationActions } from "@vega-api/custom/stores/organisations.store";

import { CommonPaths } from "../../paths";
import { REDIRECT_STORAGE_KEY } from "../QueryRedirectHandler/QueryRedirectHandler";

type LocationLike = {
  pathname: string;
  search: string;
  hash: string;
};

const popSavedRedirect = (): string | null => {
  const saved = localStorage.getItem(REDIRECT_STORAGE_KEY);
  localStorage.removeItem(REDIRECT_STORAGE_KEY);

  return saved;
};

const buildCurrentFullPath = (loc: LocationLike): string =>
  `${loc.pathname}${loc.search}${loc.hash}`;

const resolveRedirectTarget = (
  saved: string,
  loc: LocationLike,
): string | null => {
  const currentOrigin = window.location.origin;

  try {
    const parsed = new URL(saved, currentOrigin);
    const deepLink = parsed.searchParams.get("deepLink");

    if (!deepLink || !deepLink.startsWith("/")) {
      return null;
    }

    // If a limitedPartnerId is present, persist it so org selection can occur downstream
    const limitedPartnerId = parsed.searchParams.get("limitedPartnerId");

    if (limitedPartnerId) {
      organisationActions.setOrganisationId(limitedPartnerId);
    }

    const currentFullPath = buildCurrentFullPath(loc);

    if (deepLink !== currentFullPath) {
      return deepLink;
    }

    return null;
  } catch {
    return null;
  }
};

export const useSavedRedirectHandler = () => {
  const navigate = useNavigate();

  const handleSavedRedirectOnHome = useCallback(
    (currentLocation: LocationLike) => {
      if (!matchPath(currentLocation.pathname, CommonPaths.Home)) {
        return;
      }

      const savedRedirect = popSavedRedirect();

      if (!savedRedirect) {
        return;
      }

      const targetRedirect = resolveRedirectTarget(
        savedRedirect,
        currentLocation,
      );

      if (targetRedirect) {
        navigate(targetRedirect, { replace: true });
      }
    },
    [navigate],
  );

  return { handleSavedRedirectOnHome };
};
